import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ocppMessageType } from '../../../../../../config/enum';
import MyButton from '../../../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../../../shared-components/custom-input/CustomInput';
import SelectField from '../../../../../../shared-components/select-field/SelectField';
import ConvertDateTimeToUTC from '../../../../../../shared-functions/ConvertDateTimeToUTC';

interface ChargingSchedulePeriod {
  startPeriod: number | null;
  limit: number | null;
  numberPhases: number | null;
}

interface ChargingProfileProperties {
  chargingProfileId: number | null;
  stackLevel: number | null;
  chargingProfilePurpose: string;
  chargingProfileKind: string;
  recurrencyKind: string;
  validFromDate: string; // ISO timestamp or date string
  validToDate: string; // ISO timestamp or date string
  duration: number | null;
  startScheduleDate: string; // ISO timestamp
  chargingRateUnit: string;
  chargingSchedulePeriod: ChargingSchedulePeriod[];
  minChargingRate: number | null;
}

interface SetChargingProfileProperties {
  handleApiCall: (type: string, body: any) => void;
  buttonLoading?: boolean;
}

/**
 * Component to execute the command set charging profile
 * @param {SetChargingProfileProperties} properties - The properties
 * @returns {React.ReactElement} The result
 */
export default function SetChargingProfile(
  properties: SetChargingProfileProperties,
): React.ReactElement {
  const { t } = useTranslation('devices');
  const { handleApiCall, buttonLoading } = properties;

  const chargingSchedulePeriodReference = useRef<HTMLDivElement>(null);

  const [chargingProfile, setChargingProfile] = useState<ChargingProfileProperties>({
    chargingProfileId: null, // Number or null
    stackLevel: null, // Number or null
    chargingProfilePurpose: '',
    chargingProfileKind: '',
    recurrencyKind: '',
    validFromDate: '',
    validToDate: '',
    // chargingSchedule
    duration: null, // Number or null,
    startScheduleDate: '',
    chargingRateUnit: '',
    // chargingSchedulePeriod
    chargingSchedulePeriod: [
      {
        startPeriod: null,
        limit: null,
        numberPhases: null,
      },
    ],
    minChargingRate: null,
  });

  /**
   * Function to update charging point inputs
   * @param {string} name - The name
   * @param {string | number} value - The value
   */
  const handleChangeInputs = (name: string, value: string | number) => {
    setChargingProfile((previousDetails) => ({
      ...previousDetails,
      [name]: value,
    }));
  };

  /**
   * Function to add schedule period
   */
  const handleAddSchedulePeriod = () => {
    setChargingProfile((previousDetails) => ({
      ...previousDetails,
      chargingSchedulePeriod: [
        ...previousDetails.chargingSchedulePeriod,
        {
          startPeriod: null,
          limit: null,
          numberPhases: null,
        },
      ],
    }));

    // Scroll to bottom of the container, because we are adding new charging schedule period at the end of the list
    if (chargingSchedulePeriodReference.current) {
      chargingSchedulePeriodReference.current.scrollTop =
        chargingSchedulePeriodReference.current.scrollHeight;
    }
  };

  /**
   * Function to remove schedule period by index
   * @param {number} indexToRemove - The indexToRemove
   */
  const handleRemoveSchedulePeriod = (indexToRemove: number) => {
    setChargingProfile((previousDetails) => ({
      ...previousDetails,
      chargingSchedulePeriod: previousDetails.chargingSchedulePeriod.filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  /**
   * Function to update the values of charging schedule data
   * @param {number} index - The index
   * @param {string} name - The name
   * @param {string | number} value - The value
   */
  const handleChangeSchedulePeriodValue = (index: number, name: string, value: string | number) => {
    setChargingProfile((previousDetails) => {
      const updatedPeriods = [...previousDetails.chargingSchedulePeriod];
      updatedPeriods[index] = {
        ...updatedPeriods[index],
        [name]: value,
      };

      return {
        ...previousDetails,
        chargingSchedulePeriod: updatedPeriods,
      };
    });
  };

  /**
   * Handle submit command
   */
  const handleSubmitCommand = async () => {
    const body = {
      connectorId: 0, // If connectorId = 0, the message contains an overall limit for the Charge Point.
      csChargingProfiles: {
        chargingProfileId: parseInt(String(chargingProfile.chargingProfileId), 10),
        stackLevel: parseInt(String(chargingProfile.stackLevel), 10),
        chargingProfilePurpose: chargingProfile.chargingProfilePurpose,
        chargingProfileKind: chargingProfile.chargingProfileKind,
        recurrencyKind: chargingProfile.recurrencyKind || null,
        validFrom: chargingProfile.validFromDate
          ? ConvertDateTimeToUTC(chargingProfile.validFromDate)
          : '',
        validTo: chargingProfile.validToDate
          ? ConvertDateTimeToUTC(chargingProfile.validToDate)
          : '',
        chargingSchedule: {
          duration: parseInt(String(chargingProfile.duration), 10),
          startSchedule: chargingProfile.startScheduleDate
            ? ConvertDateTimeToUTC(chargingProfile.startScheduleDate)
            : '',
          chargingRateUnit: chargingProfile.chargingRateUnit || 'W',
          chargingSchedulePeriod: chargingProfile.chargingSchedulePeriod.map((period) => ({
            startPeriod: parseInt(String(period.startPeriod), 10),
            limit: parseFloat(String(period.limit)),
            numberPhases: parseInt(String(period.numberPhases), 10),
          })),
          minChargingRate: parseFloat(String(chargingProfile.minChargingRate)),
        },
      },
    };

    const requestBody = Object.fromEntries(
      Object.entries(body).filter(([, value]: [string, any]) => {
        if (value === null) return false;
        if (typeof value === 'string') return value.trim() !== '';
        return true;
      }),
    );

    handleApiCall(ocppMessageType.SET_CHARGING_PROFILE, requestBody);
  };

  return (
    <div className="ocpp-commands-set-charging-profile-wrap">
      <div
        className="ocpp-commands-set-charging-profile-container"
        ref={chargingSchedulePeriodReference}>
        <div className="set-charging-profile-inputs">
          <CustomInput
            name="chargingProfileId"
            inputLabel={t('chargingProfileField')}
            type="number"
            placeholder={t('chargingProfileField')}
            value={chargingProfile.chargingProfileId}
            onChangeValue={handleChangeInputs}
            valueRequired
            fontSize="small"
          />
          <CustomInput
            name="stackLevel"
            inputLabel={t('stackLevel')}
            type="number"
            placeholder={t('stackLevel')}
            value={chargingProfile.stackLevel}
            onChangeValue={handleChangeInputs}
            valueRequired
            fontSize="small"
          />
          <SelectField
            name="chargingProfilePurpose"
            inputLabel={t('chargingProfilePurpose')}
            selectedValue={chargingProfile.chargingProfilePurpose}
            placeholder={t('chargingProfilePurpose')}
            onChangeValue={handleChangeInputs}
            valueRequired
            options={[
              {
                label: 'ChargePointMaxProfile',
                value: 'ChargePointMaxProfile',
              },
              {
                label: 'TxDefaultProfile',
                value: 'TxDefaultProfile',
              },
              {
                label: 'TxProfile',
                value: 'TxProfile',
              },
            ]}
            fontSize="small"
          />
          <SelectField
            name="chargingProfileKind"
            inputLabel={t('chargingProfileKind')}
            selectedValue={chargingProfile.chargingProfileKind}
            placeholder={t('chargingProfileKind')}
            onChangeValue={handleChangeInputs}
            valueRequired
            options={[
              {
                label: 'Absolute',
                value: 'Absolute',
              },
              {
                label: 'Recurring',
                value: 'Recurring',
              },
              {
                label: 'Relative',
                value: 'Relative',
              },
            ]}
            fontSize="small"
          />
          <SelectField
            name="recurrencyKind"
            inputLabel={t('recurrencyKind')}
            selectedValue={chargingProfile.recurrencyKind}
            placeholder={t('recurrencyKind')}
            onChangeValue={handleChangeInputs}
            options={[
              {
                label: 'Daily',
                value: 'Daily',
              },
              {
                label: 'Weekly',
                value: 'Weekly',
              },
            ]}
            fontSize="small"
          />
          <CustomDatePicker
            name="validFromDate"
            inputLabel={t('validFromDate')}
            placeholder="DD/MM/YYYY 00:00:00"
            requiredTime
            value={
              chargingProfile.validFromDate?.includes(':')
                ? chargingProfile.validFromDate
                : chargingProfile.validFromDate?.split(' ')[0]
            }
            readOnly={false}
            onChangeValue={handleChangeInputs}
            fontSize="small"
          />
          <CustomDatePicker
            name="validToDate"
            inputLabel={t('validToDate')}
            placeholder="DD/MM/YYYY 00:00:00"
            requiredTime
            value={
              chargingProfile.validToDate?.includes(':')
                ? chargingProfile.validToDate
                : chargingProfile.validToDate?.split(' ')[0]
            }
            readOnly={false}
            onChangeValue={handleChangeInputs}
            fontSize="small"
            minDate={
              chargingProfile.validFromDate
                ? chargingProfile.validFromDate.split(' ')[0]
                : ''
            }
          />
          <CustomInput
            name="duration"
            inputLabel={t('durationInSeconds')}
            type="number"
            placeholder={t('durationInSeconds')}
            value={chargingProfile.duration}
            onChangeValue={handleChangeInputs}
            fontSize="small"
          />
          <CustomDatePicker
            name="startScheduleDate"
            inputLabel={t('startScheduleDate')}
            placeholder="DD/MM/YYYY 00:00:00"
            requiredTime
            value={
              chargingProfile.startScheduleDate?.includes(':')
                ? chargingProfile.startScheduleDate
                : chargingProfile.startScheduleDate?.split(' ')[0]
            }
            readOnly={false}
            onChangeValue={handleChangeInputs}
            fontSize="small"
          />
          <CustomInput
            name="minChargingRate"
            inputLabel={t('minChargingRate')}
            type="number"
            placeholder={t('minChargingRate')}
            value={chargingProfile.minChargingRate}
            onChangeValue={handleChangeInputs}
            fontSize="small"
          />
          <SelectField
            name="chargingRateUnit"
            inputLabel={t('chargingRateUnit')}
            selectedValue={chargingProfile.chargingRateUnit}
            placeholder={t('chargingRateUnit')}
            onChangeValue={handleChangeInputs}
            options={[
              {
                label: 'A',
                value: 'A',
              },
              {
                label: 'W',
                value: 'W',
              },
            ]}
            fontSize="small"
          />
        </div>
        <div className="charging-schedule-period-wrap">
          <div className="schedule-period-header">
            <h6>{t('schedulePeriod')}</h6>
            <MyButton
              label={t('addPeriod')}
              buttonType="primary-outline"
              onClickFunc={handleAddSchedulePeriod}
            />
          </div>
          {chargingProfile.chargingSchedulePeriod?.map((eachSchedulePeriod, index) => (
            <div
              key={`charging-schedule-period-${index}`}
              className="each-charging-schedule-period-wrap">
              <div className="schedule-period-inputs-container">
                <CustomInput
                  name="startPeriod"
                  inputLabel={t('startPeriod')}
                  type="number"
                  placeholder={t('startPeriod')}
                  value={eachSchedulePeriod.startPeriod}
                  onChangeValue={(name: string, value: string | number) =>
                    handleChangeSchedulePeriodValue(index, name, value)
                  }
                  fontSize="small"
                />
                <CustomInput
                  name="limit"
                  inputLabel={t('limit')}
                  type="number"
                  placeholder={t('limit')}
                  value={eachSchedulePeriod.limit}
                  onChangeValue={(name: string, value: string | number) =>
                    handleChangeSchedulePeriodValue(index, name, value)
                  }
                  fontSize="small"
                />
                <CustomInput
                  name="numberPhases"
                  inputLabel={t('numberPhases')}
                  type="number"
                  placeholder={t('ifEmptyThreeAssumed')}
                  value={eachSchedulePeriod.numberPhases}
                  onChangeValue={(name: string, value: string | number) =>
                    handleChangeSchedulePeriodValue(index, name, value)
                  }
                  fontSize="small"
                />
              </div>
              <MyButton
                label={t('delete')}
                buttonType="error"
                onClickFunc={() => handleRemoveSchedulePeriod(index)}
                disableButton={chargingProfile.chargingSchedulePeriod.length === 1}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="data-management-form-buttons">
        <MyButton label={t('back')} buttonType="back" onClickFunc={() => {}} />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          loading={buttonLoading}
          disableButton={buttonLoading}
          onClickFunc={handleSubmitCommand}
        />
      </div>
    </div>
  );
}
